import { useEffect, useRef, useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';

const EMAILJS_SERVICE_ID = 'service_faraci';
const EMAILJS_TEMPLATE_ID = 'template_b4wktrg';
const EMAILJS_PUBLIC_KEY = 'QDZvBZk44pFlDoP7J';

const contactInfo = [
  {
    id: 1,
    icon: MapPin,
    title: 'Dirección',
    content: 'La Calesa No 24, Int 5-B Col. Ixtlahuacan de los Membrillos, Jalisco CP.45877',
  },
  {
    id: 2,
    icon: Phone,
    title: 'Teléfono',
    content: '+52 33 2246 8176',
  },
  {
    id: 3,
    icon: Mail,
    title: 'Email',
    content: 'contacto@faraci.com.mx',
  },
  {
    id: 4,
    icon: Clock,
    title: 'Horario',
    content: 'Lun - Vie: 9:00 AM - 6:00 PM',
  },
];

export const ContactSection = () => {
  const sectionRef = useRef(null);
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        EMAILJS_PUBLIC_KEY
      );

      if (result.status === 200) {
        toast.success('¡Mensaje enviado exitosamente!', {
          description: 'Nos pondremos en contacto contigo pronto.',
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      }
    } catch (error) {
      console.error('EmailJS Error:', error);
      toast.error('Error al enviar el mensaje', {
        description: 'Por favor intenta de nuevo o contáctanos directamente.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contacto"
      ref={sectionRef}
      data-testid="contact-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 reveal">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="gold-line" />
            <span className="font-manrope uppercase tracking-widest text-xs font-bold text-[#BF9000]">
              Hablemos de tu proyecto
            </span>
            <div className="gold-line" />
          </div>
          <h2
            data-testid="contact-title"
            className="font-oswald text-3xl sm:text-4xl lg:text-5xl font-bold text-[#385723] uppercase tracking-tight mb-4"
          >
            Contáctanos
          </h2>
          <p className="font-manrope text-slate-600 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Estamos listos para convertir tu visión en realidad
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-5 reveal-left">
            <div className="bg-[#385723] p-8 h-full">
              <h3 className="font-oswald text-2xl font-bold text-white uppercase tracking-tight mb-8">
                Información de <span className="text-[#92D050]">Contacto</span>
              </h3>

              <div className="space-y-6">
                {contactInfo.map((info) => {
                  const IconComponent = info.icon;
                  return (
                    <div
                      key={info.id}
                      data-testid={`contact-info-${info.id}`}
                      className="contact-item flex items-start gap-4 text-white"
                    >
                      <div className="w-12 h-12 bg-[#92D050] text-[#385723] flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-oswald text-sm uppercase tracking-wider text-[#92D050] mb-1">
                          {info.title}
                        </h4>
                        {info.id === 3 ? (
                          <a 
                            href="mailto:contacto@faraci.com.mx"
                            className="font-manrope text-slate-200 text-sm leading-relaxed hover:text-[#92D050] transition-colors"
                          >
                            {info.content}
                          </a>
                        ) : info.id === 2 ? (
                          <a 
                            href="tel:+523322468176"
                            className="font-manrope text-slate-200 text-sm leading-relaxed hover:text-[#92D050] transition-colors"
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="font-manrope text-slate-200 text-sm leading-relaxed">
                            {info.content}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Google Maps */}
              <div className="mt-8 h-48 overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7476.602031547456!2d-103.24832826120016!3d20.45282615154668!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x842f49186cee278d%3A0xae69c04f7b237d68!2sCalesa%2024-Int%205-B%2C%2045877%20Las%20Carretas%2C%20Jal.!5e0!3m2!1ses!2smx!4v1775879482769!5m2!1ses!2smx"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación Faraci Edificaciones"
                  data-testid="contact-map-iframe"
                />
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7 reveal-right">
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-[#F8FAFC] p-8"
            >
              <h3 className="font-oswald text-2xl font-bold text-[#385723] uppercase tracking-tight mb-8">
                Envíanos un <span className="text-[#92D050]">Mensaje</span>
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label
                    htmlFor="name"
                    className="font-manrope text-sm font-semibold text-[#385723] uppercase tracking-wider"
                  >
                    Nombre Completo *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    data-testid="contact-input-name"
                    className="mt-2 border-slate-300 focus:border-[#385723] focus:ring-[#385723] bg-white h-12"
                    placeholder="Tu nombre"
                    required
                  />
                </div>
                <div>
                  <Label
                    htmlFor="email"
                    className="font-manrope text-sm font-semibold text-[#385723] uppercase tracking-wider"
                  >
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    data-testid="contact-input-email"
                    className="mt-2 border-slate-300 focus:border-[#385723] focus:ring-[#385723] bg-white h-12"
                    placeholder="tu@email.com"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label
                    htmlFor="phone"
                    className="font-manrope text-sm font-semibold text-[#385723] uppercase tracking-wider"
                  >
                    Teléfono
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    data-testid="contact-input-phone"
                    className="mt-2 border-slate-300 focus:border-[#385723] focus:ring-[#385723] bg-white h-12"
                    placeholder="+52 33 0000 0000"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="subject"
                    className="font-manrope text-sm font-semibold text-[#385723] uppercase tracking-wider"
                  >
                    Asunto *
                  </Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleInputChange}
                    data-testid="contact-input-subject"
                    className="mt-2 border-slate-300 focus:border-[#385723] focus:ring-[#385723] bg-white h-12"
                    placeholder="Tema de consulta"
                    required
                  />
                </div>
              </div>

              <div className="mb-6">
                <Label
                  htmlFor="message"
                  className="font-manrope text-sm font-semibold text-[#385723] uppercase tracking-wider"
                >
                  Mensaje *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  data-testid="contact-input-message"
                  className="mt-2 border-slate-300 focus:border-[#385723] focus:ring-[#385723] bg-white min-h-[150px]"
                  placeholder="Cuéntanos sobre tu proyecto..."
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-button"
                className="w-full bg-[#385723] text-white hover:bg-[#2a421b] px-8 py-6 font-oswald uppercase tracking-wider text-lg transition-all duration-300 hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Enviar Mensaje
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
